import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { API_BUSINESS_URL } from '@/utils';
import { PaginatedResponse } from '@/types';
import { ShippingStatusApi, ShippingStatus } from '../models';

interface ShippingStatusOptionApi {
  id: string;
  name: string;
  commercialName: string | null;
  shippingLevel: string | null;
}

interface ShippingStatusesPaginatedResponse {
  count: number;
  items: ShippingStatusApi[];
  offset: number;
  limit: number;
}

const shippingStatusesService = createApi({
  reducerPath: 'shippingStatusesService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}/shipping-statuses`),
  tagTypes: ['ShippingStatuses'],
  endpoints: (builder) => ({
    getShippingStatuses: builder.query<
      PaginatedResponse<ShippingStatus>,
      { limit?: number; offset?: number }
    >({
      query: ({ limit = 1000, offset = 0 } = {}) => ({
        url: '',
        params: {
          limit,
          offset,
        },
      }),
      transformResponse: (response: ShippingStatusesPaginatedResponse) => {
        if (!response || !response.items || !Array.isArray(response.items)) {
          return {
            count: 0,
            items: [],
          };
        }
        return {
          count: response.count || 0,
          items: response.items.map((status) => ({
            id: status.id,
            name: status.name,
            commercialName: status.commercialName,
            commercialExplanation: status.commercialExplanation,
            status: status.status,
            externalId: status.externalId,
            ruleId: status.ruleId,
            parentId: status.parentId,
            createdAt: status.createdAt,
            updatedAt: status.updatedAt,
          })),
        };
      },
      providesTags: ['ShippingStatuses'],
    }),
    /**
     * Combos de filtro: solo id/nombre. El endpoint devuelve un array plano,
     * lo envolvemos en { count, items } para no tocar los consumidores.
     */
    getShippingStatusOptions: builder.query<
      PaginatedResponse<ShippingStatus>,
      { limit?: number; offset?: number } | void
    >({
      query: () => ({ url: '/options' }),
      transformResponse: (response: ShippingStatusOptionApi[]) => {
        const items = Array.isArray(response) ? response : [];
        return {
          count: items.length,
          items: items.map((status) => ({
            id: status.id,
            name: status.name,
            commercialName: status.commercialName ?? undefined,
          })),
        };
      },
      providesTags: ['ShippingStatuses'],
    }),
    getShippingStatusById: builder.query<
      ShippingStatus,
      { shippingStatusId: string }
    >({
      query: ({ shippingStatusId }) => ({
        url: `/${shippingStatusId}`,
      }),
      transformResponse: (response: ShippingStatusApi) => ({
        id: response.id,
        name: response.name,
        commercialName: response.commercialName,
        commercialExplanation: response.commercialExplanation,
        status: response.status,
        externalId: response.externalId,
        ruleId: response.ruleId,
        parentId: response.parentId,
        createdAt: response.createdAt,
        updatedAt: response.updatedAt,
      }),
      providesTags: (_result, _error, { shippingStatusId }) => [
        { type: 'ShippingStatuses' as const, id: shippingStatusId },
      ],
    }),
  }),
});

export default shippingStatusesService;
export const {
  useGetShippingStatusesQuery,
  useGetShippingStatusOptionsQuery,
  useGetShippingStatusByIdQuery,
} = shippingStatusesService;
